import { PeoplePicker } from '@/components/PeoplePicker';
import { Avatar, AvatarStack, EmptyState, ErrorNotice, Spinner } from '@/components/ui';
import { useChannels, useOpenDirect } from '@/hooks/queries';
import { useChat } from '@/hooks/use-chat';
import { formatMessageTime } from '@/lib/design';
import { useAuth } from '@/stores/auth';
import type { ChatChannel, ChatMessage } from '@/types/api';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';

export function ChatPage() {
  const currentUser = useAuth((state) => state.user);
  const { data: channels, isLoading, error, refetch } = useChannels();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [pickingPerson, setPickingPerson] = useState(false);
  const openDirect = useOpenDirect();

  // Al entrar se abre el canal con actividad mas reciente.
  useEffect(() => {
    if (!activeId && channels && channels.length > 0) {
      setActiveId(channels[0].id);
    }
  }, [channels, activeId]);

  const active = channels?.find((c) => c.id === activeId) ?? null;
  const { messages, loading, typing, send, notifyTyping } = useChat(activeId);

  const grouped = useMemo(() => groupChannels(channels ?? []), [channels]);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-start gap-3 p-6">
        <ErrorNotice>No se pudieron cargar tus conversaciones: {(error as Error).message}</ErrorNotice>
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-control border border-line-strong px-3 py-1.5 text-[13px] font-semibold transition-colors hover:border-line-hover"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1">
      <nav
        aria-label="Canales"
        className="flex w-[232px] flex-none flex-col overflow-auto border-r border-line bg-sunken"
      >
        <div className="flex items-center gap-2 px-3.5 pb-1.5 pt-3.5">
          <h2 className="section-label flex-1 text-faint">Canales</h2>
        </div>
        {grouped.rooms.length === 0 && (
          <p className="px-3.5 pb-2 text-[11.5px] text-faint text-pretty">
            Los canales aparecen al entrar a un proyecto.
          </p>
        )}
        {grouped.rooms.map((channel) => (
          <ChannelRow
            key={channel.id}
            channel={channel}
            active={channel.id === activeId}
            onSelect={() => setActiveId(channel.id)}
          />
        ))}

        <div className="flex items-center gap-2 px-3.5 pb-1.5 pt-4">
          <h2 className="section-label flex-1 text-faint">Mensajes directos</h2>
          <button
            type="button"
            onClick={() => setPickingPerson(true)}
            title="Escribirle a alguien"
            aria-label="Escribirle a alguien"
            className="flex size-5 flex-none items-center justify-center rounded-md border border-line-strong text-[13px] leading-none text-subtle transition-colors hover:border-brand hover:text-brand-deep"
          >
            +
          </button>
        </div>

        {grouped.directs.length === 0 && (
          <p className="px-3.5 pb-2 text-[11.5px] text-faint text-pretty">
            Usa el + para escribirle a cualquiera del equipo.
          </p>
        )}
        {grouped.directs.map((channel) => (
          <ChannelRow
            key={channel.id}
            channel={channel}
            active={channel.id === activeId}
            onSelect={() => setActiveId(channel.id)}
            currentUserId={currentUser?.id}
          />
        ))}
      </nav>

      <section className="flex min-w-0 flex-1 flex-col bg-surface">
        {!active && (
          <div className="flex flex-1 items-center justify-center p-8">
            <EmptyState
              title="Elige una conversacion"
              hint="Los canales de proyecto aparecen solos; para un mensaje directo usa el + de la bandeja."
            />
          </div>
        )}

        {active && (
          <header className="flex h-[52px] flex-none items-center gap-2.5 border-b border-line px-[18px]">
            <h2 className="font-display text-[15px] font-semibold">
              {channelTitle(active, currentUser?.id)}
            </h2>
            {active.topic && <p className="text-[12.5px] text-subtle">{active.topic}</p>}
            <div className="flex-1" />
            <AvatarStack users={active.members.map((m) => m.user)} max={4} size={24} />
            <p className="text-[12.5px] text-subtle">
              {active.members.length} {active.members.length === 1 ? 'persona' : 'personas'}
            </p>
          </header>
        )}

        <MessageList messages={messages} loading={loading} />

        {typing.length > 0 && (
          <p aria-live="polite" className="px-[22px] pb-1 text-[11.5px] italic text-faint">
            {typing.map((p) => p.fullName.split(' ')[0]).join(', ')}{' '}
            {typing.length === 1 ? 'esta escribiendo…' : 'estan escribiendo…'}
          </p>
        )}

        {active && (
          <Composer
            channel={active}
            currentUserId={currentUser?.id}
            onSend={send}
            onTyping={notifyTyping}
          />
        )}
      </section>

      {pickingPerson && (
        <PeoplePicker
          title="Nuevo mensaje"
          description="Se abre la conversacion, o se reanuda si ya existe."
          confirmLabel="Escribir"
          excludeIds={currentUser ? [currentUser.id] : []}
          emptyHint="No hay nadie mas en la plataforma todavia."
          pending={openDirect.isPending}
          error={openDirect.error ? (openDirect.error as Error).message : null}
          onClose={() => setPickingPerson(false)}
          onPick={(person) =>
            openDirect.mutate(person.id, {
              onSuccess: (channel) => {
                setActiveId(channel.id);
                setPickingPerson(false);
              },
            })
          }
        />
      )}
    </div>
  );
}

function MessageList({ messages, loading }: { messages: ChatMessage[]; loading: boolean }) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Al llegar un mensaje la vista baja sola, como en cualquier chat.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-[22px] py-[18px]">
      {messages.length === 0 && (
        <p className="my-auto text-center text-[13px] text-faint">
          Nadie ha escrito todavia. Rompe el hielo.
        </p>
      )}

      {messages.map((message) => (
        <article
          key={message.id}
          className={`flex items-start gap-[11px] transition-opacity ${
            message.pending ? 'opacity-55' : ''
          }`}
        >
          <Avatar user={message.author} size={30} />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex items-baseline gap-2">
              <span className="text-[13.5px] font-bold">{message.author.fullName}</span>
              {message.pending ? (
                <span className="text-[11px] text-fainter">enviando…</span>
              ) : message.failed ? (
                <span className="text-[11px] font-semibold text-danger-text">
                  no se pudo enviar
                </span>
              ) : (
                <time className="text-[11px] text-fainter" dateTime={message.createdAt}>
                  {formatMessageTime(message.createdAt)}
                </time>
              )}
            </div>
            <p className="whitespace-pre-wrap text-[13.5px] leading-[1.5] text-body text-pretty">
              {message.body}
            </p>
          </div>
        </article>
      ))}
      <div ref={bottomRef} />
    </div>
  );
}

function Composer({
  channel,
  currentUserId,
  onSend,
  onTyping,
}: {
  channel: ChatChannel | null;
  currentUserId?: string;
  onSend: (body: string) => void;
  onTyping: () => void;
}) {
  const [draft, setDraft] = useState('');
  const canSend = draft.trim().length > 0;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canSend) {
      return;
    }
    onSend(draft);
    setDraft('');
  }

  const placeholder = channel
    ? `Mensaje en ${channelTitle(channel, currentUserId)}…`
    : 'Selecciona un canal';

  return (
    <form onSubmit={handleSubmit} className="flex-none px-[22px] pb-[18px] pt-3">
      <div className="overflow-hidden rounded-panel border border-line-strong bg-surface shadow-[0_1px_3px_rgba(0,0,0,.35)] focus-within:border-brand">
        <input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            onTyping();
          }}
          placeholder={placeholder}
          aria-label="Escribe un mensaje"
          disabled={!channel}
          className="w-full bg-transparent px-3.5 py-3 text-[13.5px] outline-none placeholder:text-faint"
        />
        <div className="flex items-center gap-2.5 border-t border-line-soft px-3 py-2 text-[13px] text-faint">
          <span aria-hidden="true">＠</span>
          <span aria-hidden="true">📎</span>
          <span aria-hidden="true">😊</span>
          <span className="text-[11.5px]">Enter para enviar</span>
          <div className="flex-1" />
          <button
            type="submit"
            disabled={!canSend}
            className={`rounded-[7px] px-3.5 py-1.5 text-[12.5px] font-semibold transition-colors ${
              canSend
                ? 'bg-brand text-brand-on hover:bg-brand-strong'
                : 'bg-muted-surface text-fainter'
            }`}
          >
            Enviar
          </button>
        </div>
      </div>
    </form>
  );
}

function ChannelRow({
  channel,
  active,
  onSelect,
  currentUserId,
}: {
  channel: ChatChannel;
  active: boolean;
  onSelect: () => void;
  currentUserId?: string;
}) {
  const isDirect = channel.type === 'DIRECT';
  const other = isDirect
    ? channel.members.find((m) => m.user.id !== currentUserId)?.user ?? null
    : null;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? 'true' : undefined}
      className={`flex items-center gap-2 px-3.5 py-[7px] text-left text-[13px] transition-colors ${
        active
          ? 'border-r-2 border-brand bg-brand-active font-semibold text-brand-deep'
          : 'text-body hover:bg-canvas'
      }`}
    >
      {isDirect ? (
        <Avatar user={other} size={20} />
      ) : (
        <span aria-hidden="true" className="text-faint">
          #
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">
        {channel.displayName ?? channel.name ?? 'Conversacion'}
      </span>
      {channel.unreadCount > 0 && !active && (
        <span className="rounded-[10px] bg-brand px-1.5 py-px text-[10.5px] font-bold text-brand-on">
          {channel.unreadCount}
        </span>
      )}
    </button>
  );
}

function channelTitle(channel: ChatChannel, currentUserId?: string): string {
  if (channel.type === 'DIRECT') {
    return (
      channel.members.find((m) => m.user.id !== currentUserId)?.user.fullName ??
      channel.displayName ??
      'Conversacion'
    );
  }
  return `#${channel.name ?? channel.displayName ?? 'canal'}`;
}

function groupChannels(channels: ChatChannel[]): { rooms: ChatChannel[]; directs: ChatChannel[] } {
  return {
    rooms: channels.filter((c) => c.type !== 'DIRECT'),
    directs: channels.filter((c) => c.type === 'DIRECT'),
  };
}
